import {loadScript, PICKER_FAILED, type PickedFile} from './cloudConnect';

// The Google Picker, opened with a short-lived token the server mints from the PM's
// stored grant. Script handling mirrors shared/components/google-picker/google_picker_new.js;
// that component's own GIS token client is not used here.
const GAPI_SRC = 'https://apis.google.com/js/api.js';

type PickerData = Record<string, unknown>;
type DocsView = {
	setIncludeFolders(on: boolean): DocsView;
	setSelectFolderEnabled(on: boolean): DocsView;
	setEnableDrives(on: boolean): DocsView;
};
type PickerBuilder = {
	addView(view: DocsView): PickerBuilder;
	setOAuthToken(token: string): PickerBuilder;
	setDeveloperKey(key: string): PickerBuilder;
	setAppId(appId: string): PickerBuilder;
	setCallback(callback: (data: PickerData) => void): PickerBuilder;
	build(): {setVisible(visible: boolean): void};
};
type PickerNamespace = {
	PickerBuilder: new () => PickerBuilder;
	DocsView: new (viewId: string) => DocsView;
	ViewId: {DOCS: string};
	Action: {PICKED: string; CANCEL: string};
	Response: {ACTION: string; DOCUMENTS: string};
	Document: {ID: string; NAME: string};
};
type GoogleGlobals = {
	gapi?: {load(api: string, options: {callback: () => void; onerror: () => void}): void};
	google?: {picker?: PickerNamespace};
};

const globals = window as unknown as GoogleGlobals;

const loadPickerApi = (): Promise<PickerNamespace> => {
	if (globals.google?.picker) return Promise.resolve(globals.google.picker);
	const gapiReady = globals.gapi ? Promise.resolve() : loadScript(GAPI_SRC);
	return gapiReady.then(
		() =>
			new Promise<PickerNamespace>((resolve, reject) => {
				const fail = () => reject(new Error(PICKER_FAILED));
				if (!globals.gapi) return fail();
				globals.gapi.load('picker', {
					callback: () => (globals.google?.picker ? resolve(globals.google.picker) : fail()),
					onerror: fail,
				});
			})
	);
};

/**
 * Opens the picker over the page and resolves the picked file, or null on cancel. `appId` (the
 * Cloud project number) is what lets the server's drive.file grant open the file afterwards.
 * Rejects with PICKER_FAILED when the picker cannot load.
 */
export const pickGoogleDriveFile = ({
	accessToken,
	developerKey,
	appId,
}: {
	accessToken: string;
	developerKey: string;
	appId: string;
}): Promise<PickedFile | null> =>
	loadPickerApi().then(
		picker =>
			new Promise<PickedFile | null>((resolve, reject) => {
				const onPicked = (data: PickerData) => {
					const action = data[picker.Response.ACTION];
					if (action === picker.Action.CANCEL) return resolve(null);
					// Anything else (the picker's own "loaded" notification) is not an answer yet.
					if (action !== picker.Action.PICKED) return;
					const documents = data[picker.Response.DOCUMENTS] as Array<Record<string, unknown>> | undefined;
					const document = documents?.[0];
					const id = document?.[picker.Document.ID];
					if (typeof id !== 'string' || !id) return resolve(null);
					resolve({externalFileId: id, name: String(document?.[picker.Document.NAME] ?? '')});
				};
				try {
					const view = () =>
						new picker.DocsView(picker.ViewId.DOCS).setIncludeFolders(true).setSelectFolderEnabled(false);
					// My Drive, then Shared drives (the server reads files with supportsAllDrives).
					new picker.PickerBuilder()
						.addView(view())
						.addView(view().setEnableDrives(true))
						.setOAuthToken(accessToken)
						.setDeveloperKey(developerKey)
						.setAppId(appId)
						.setCallback(onPicked)
						.build()
						.setVisible(true);
				} catch {
					reject(new Error(PICKER_FAILED));
				}
			}),
		() => {
			throw new Error(PICKER_FAILED);
		}
	);
